import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { trackPageView } from '../../firebase/trackingService';
import SEO from '../common/SEO';

const PageContainer = styled.section`
  background-color: #121212;
  color: #FFFFFF;
  padding: 120px 24px 80px;
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding: 100px 24px 60px;
  }
`;

const Content = styled(motion.div)`
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.7;
`;

const Title = styled.h1`
  font-size: 42px;
  color: #2196F3;
  margin-bottom: 16px;
  font-weight: 700;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const LastUpdated = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 48px;
  font-style: italic;
`;

const Section = styled.section`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  color: #FFD700;
  margin-bottom: 16px;
  font-weight: 600;
`;

const SubSectionTitle = styled.h3`
  font-size: 20px;
  color: #FFFFFF;
  margin: 24px 0 12px;
  font-weight: 600;
`;

const Paragraph = styled.p`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 16px;
`;

const List = styled.ul`
  margin: 16px 0;
  padding-left: 24px;
`;

const ListItem = styled.li`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 8px;
`;

const ContactInfo = styled.div`
  background-color: #1E1E1E;
  border-radius: 12px;
  padding: 24px;
  margin: 32px 0;
  border: 1px solid #333;
`;

const HighlightBox = styled.div`
  background-color: rgba(33, 150, 243, 0.1);
  border: 1px solid #2196F3;
  border-radius: 8px;
  padding: 20px;
  margin: 24px 0;
  
  p {
    margin-bottom: 0;
    color: #FFFFFF;
    font-weight: 500;
  }
`;

const PrivacyPolicy = () => {
  useEffect(() => {
    trackPageView('Privacy Policy', window.location.pathname);
  }, []);

  return (
    <>
      <SEO 
        title="Privacy Policy - CarConsul"
        description="Privacy Policy for CarConsul's AI-powered car buying advisor platform - how we protect and use your data"
        pagePath="privacy-policy"
      />
      <PageContainer>
        <Content
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Title>Privacy Policy</Title>
          <LastUpdated>Last Updated: December 30, 2024</LastUpdated>

          <Section>
            <SectionTitle>1. Introduction</SectionTitle>
            <Paragraph>
              This Privacy Policy describes how CarConsul ("we", "us", or "our") collects, uses, and shares your personal information when you visit or make a purchase from https://carconsul.com (the "Site").
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>2. Definitions</SectionTitle>
            <List>
              <ListItem><strong>Personal Data:</strong> Any information that directly, indirectly, or in connection with other information allows for the identification of a natural person.</ListItem>
              <ListItem><strong>Usage Data:</strong> Data collected automatically, generated by the use of the Service or from the Service infrastructure itself.</ListItem>
              <ListItem><strong>Cookies:</strong> Small pieces of data stored on your device.</ListItem>
              <ListItem><strong>Data Controller:</strong> The natural or legal person who determines the purposes and means of the processing of Personal Data.</ListItem>
              <ListItem><strong>Data Processor:</strong> The natural or legal person who processes Personal Data on behalf of the Data Controller.</ListItem>
              <ListItem><strong>Data Subject:</strong> The natural person whose Personal Data is processed.</ListItem>
              <ListItem><strong>User:</strong> The individual using our Service.</ListItem>
              <ListItem><strong>Service:</strong> The https://carconsul.com website operated by CarConsul.</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>3. Information Collection and Use</SectionTitle>
            <Paragraph>
              We collect several different types of information for various purposes to provide and improve our Service to you.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>4. Types of Data Collected</SectionTitle>
            <SubSectionTitle>Personal Data</SubSectionTitle>
            <Paragraph>
              While using our Service, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you ("Personal Data"). Personally identifiable information may include, but is not limited to:
            </Paragraph>
            <List>
              <ListItem>Email address</ListItem>
              <ListItem>First name and last name</ListItem>
              <ListItem>Phone number</ListItem>
              <ListItem>Address, State, Province, ZIP/Postal code, City</ListItem>
              <ListItem>Cookies and Usage Data</ListItem>
            </List>

            <SubSectionTitle>Usage Data</SubSectionTitle>
            <Paragraph>
              We may also collect information on how the Service is accessed and used ("Usage Data"). This Usage Data may include information such as your computer's Internet Protocol address (e.g. IP address), browser type, browser version, the pages of our Service that you visit, the time and date of your visit, the time spent on those pages, unique device identifiers and other diagnostic data.
            </Paragraph>

            <SubSectionTitle>Tracking Cookies Data</SubSectionTitle>
            <Paragraph>
              We use cookies and similar tracking technologies to track the activity on our Service and we hold certain information. Cookies are files with a small amount of data which may include an anonymous unique identifier.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>5. Use of Data</SectionTitle>
            <Paragraph>
              CarConsul uses the collected data for various purposes:
            </Paragraph>
            <List>
              <ListItem>To provide and maintain our Service</ListItem>
              <ListItem>To notify you about changes to our Service</ListItem>
              <ListItem>To allow you to participate in interactive features of our Service when you choose to do so</ListItem>
              <ListItem>To provide customer support</ListItem>
              <ListItem>To gather analysis or valuable information so that we can improve our Service</ListItem>
              <ListItem>To monitor the usage of our Service</ListItem>
              <ListItem>To detect, prevent and address technical issues</ListItem>
              <ListItem>To provide you with news, special offers and general information about other goods, services and events which we offer</ListItem>
              <ListItem>To provide AI-powered car buying recommendations and market analysis</ListItem>
              <ListItem>To process payments and manage subscriptions</ListItem>
              <ListItem>To improve our algorithms and service quality</ListItem>
              <ListItem>To conduct research and analytics</ListItem>
              <ListItem>To prevent fraud and ensure security</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>6. Retention of Data</SectionTitle>
            <Paragraph>
              CarConsul will retain your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your Personal Data to the extent necessary to comply with our legal obligations (for example, if we are required to retain your data to comply with applicable laws), resolve disputes, and enforce our legal agreements and policies.
            </Paragraph>
            <Paragraph>
              CarConsul will also retain Usage Data for internal analysis purposes. Usage Data is generally retained for a shorter period of time, except when this data is used to strengthen the security or to improve the functionality of our Service, or we are legally obligated to retain this data for longer time periods.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>7. Transfer of Data</SectionTitle>
            <Paragraph>
              Your information, including Personal Data, may be transferred to — and maintained on — computers located outside of your state, province, country or other governmental jurisdiction where the data protection laws may differ than those from your jurisdiction.
            </Paragraph>
            <Paragraph>
              If you are located outside United States and choose to provide information to us, please note that we transfer the data, including Personal Data, to United States and process it there. Your consent to this Privacy Policy followed by your submission of such information represents your agreement to that transfer.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>8. Disclosure of Data</SectionTitle>
            <Paragraph>
              CarConsul may disclose your Personal Data in the good faith belief that such action is necessary to:
            </Paragraph>
            <List>
              <ListItem>To comply with a legal obligation</ListItem>
              <ListItem>To protect and defend the rights or property of CarConsul</ListItem>
              <ListItem>To prevent or investigate possible wrongdoing in connection with the Service</ListItem>
              <ListItem>To protect the personal safety of users of the Service or the public</ListItem>
              <ListItem>To protect against legal liability</ListItem>
            </List>
            <HighlightBox>
              <Paragraph>
                CarConsul operates independently and does not sell, rent, or share your personal data with car dealerships, manufacturers, or automotive companies for marketing purposes. We maintain our independence to provide unbiased advice.
              </Paragraph>
            </HighlightBox>
          </Section>

          <Section>
            <SectionTitle>9. Legal Basis for Processing Personal Data (GDPR)</SectionTitle>
            <Paragraph>
              If you are from the European Economic Area (EEA), CarConsul legal basis for collecting and using the personal information described in this Privacy Policy depends on the Personal Data we collect and the specific context in which we collect it.
            </Paragraph>
            <Paragraph>
              CarConsul may process your Personal Data because:
            </Paragraph>
            <List>
              <ListItem>We need to perform a contract with you</ListItem>
              <ListItem>You have given us permission to do so</ListItem>
              <ListItem>The processing is in our legitimate interests and it's not overridden by your rights</ListItem>
              <ListItem>For payment processing purposes</ListItem>
              <ListItem>To comply with the law</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>10. Your Data Protection Rights Under General Data Protection Regulation (GDPR)</SectionTitle>
            <Paragraph>
              If you are a resident of the European Economic Area (EEA), you have certain data protection rights. CarConsul aims to take reasonable steps to allow you to correct, amend, delete, or limit the use of your Personal Data.
            </Paragraph>
            <Paragraph>
              If you wish to be informed what Personal Data we hold about you and if you want it to be removed from our systems, please contact us. In certain circumstances, you have the following data protection rights:
            </Paragraph>
            <List>
              <ListItem><strong>The right to access, update or to delete</strong> the information we have on you</ListItem>
              <ListItem><strong>The right of rectification</strong> - You have the right to have your information rectified if that information is inaccurate or incomplete</ListItem>
              <ListItem><strong>The right to object</strong> - You have the right to object to our processing of your Personal Data</ListItem>
              <ListItem><strong>The right of restriction</strong> - You have the right to request that we restrict the processing of your personal information</ListItem>
              <ListItem><strong>The right to data portability</strong> - You have the right to be provided with a copy of the information we have on you in a structured, machine-readable and commonly used format</ListItem>
              <ListItem><strong>The right to withdraw consent</strong> - You also have the right to withdraw your consent at any time where CarConsul relied on your consent to process your personal information</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>11. Children's Privacy</SectionTitle>
            <Paragraph>
              Our Service does not address anyone under the age of 13 ("Children"). We do not knowingly collect personally identifiable information from anyone under the age of 13. If you are a parent or guardian and you are aware that your Children has provided us with Personal Data, please contact us. If we become aware that we have collected Personal Data from children without verification of parental consent, we take steps to remove that information from our servers.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>12. Changes to This Privacy Policy</SectionTitle>
            <Paragraph>
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page. We will let you know via email and/or a prominent notice on our Service, prior to the change becoming effective and update the "effective date" at the top of this Privacy Policy.
            </Paragraph>
            <Paragraph>
              You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>13. Contact Us</SectionTitle>
            <ContactInfo>
              <Paragraph style={{ marginBottom: '8px' }}>
                If you have any questions about this Privacy Policy, please contact us:
              </Paragraph>
              <Paragraph style={{ marginBottom: '4px' }}>
                <strong>Email:</strong> nick@carconsul.com
              </Paragraph>
              <Paragraph style={{ marginBottom: '4px' }}>
                <strong>Website:</strong> https://carconsul.com
              </Paragraph>
              <Paragraph style={{ marginBottom: '0' }}>
                <strong>Response Time:</strong> We will respond to privacy requests within 30 days
              </Paragraph>
            </ContactInfo>
          </Section>
        </Content>
      </PageContainer>
    </>
  );
};

export default PrivacyPolicy;